/* eslint-disable array-bracket-newline, no-unused-vars, react-hooks/exhaustive-deps, @typescript-eslint/no-unused-vars */
/**
 * Top traders tab of dexx trades
 */
import { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { ITopWalletsResponseItem } from '@kattana-io/common';
import TopTradersTable from './Table';
import { IStore } from '../../../reducers';
import TopTradersTableMobile from './TableMobile';
import { KattanaAPI } from '../../../store/createStore';
import { authTokenSelector } from '../../../selectors/auth';
import { actionSetTradesWallet } from '../../../actions/dex_trades';
import { ETableTimeframe, parseTableTimeframe } from '../helpers';

interface ITopTradersProps {
  width?: number;
  isMobile?: boolean;
  currentWallet?: string;
  openWalletModal?(props: any): void;
}

const TopTradersDataLayer = ({
  width,
  isMobile,
  currentWallet,
  openWalletModal,
}: ITopTradersProps) => {
  const { chain, address, exchange, flipped } = useSelector(
    (state: IStore) => state.current,
    shallowEqual
  );
  const timezone = useSelector((state: IStore) => state.timezone.current);
  const plan = useSelector((state: IStore) => state.features.currentPlan);
  const authToken = useSelector(authTokenSelector);
  const tradesWallet = useSelector(
    (state: IStore) => state.dex_trades.tradesWallet
  );
  const drawMarks = useSelector((state: IStore) => state.dex_trades.drawMarks);
  const removeMarks = useSelector(
    (state: IStore) => state.dex_trades.removeMarks
  );

  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState(ETableTimeframe.DAY);
  const [collection, setCollection] = useState<ITopWalletsResponseItem[]>([]); // filtered

  useEffect(() => {
    const controller = new AbortController();
    if (address) {
      setLoading(true);
      KattanaAPI.topTraders(
        address,
        chain,
        parseTableTimeframe(timeframe),
        authToken,
        controller
      )
        .then((res) => {
          setCollection(
            res?.map((item) => ({
              ...item,
              avg_usd_buy: flipped ? item.avg_usd_sell : item.avg_usd_buy,
              avg_usd_sell: flipped ? item.avg_usd_buy : item.avg_usd_sell,
              avg_buy: flipped ? item.avg_sell : item.avg_buy,
              avg_sell: flipped ? item.avg_buy : item.avg_sell,
            })) || []
          );
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }

    return () => controller.abort();
  }, [timeframe, address, chain, plan]);

  const onSetTimeframe = useCallback(
    (time: ETableTimeframe) => setTimeframe(time),
    [setTimeframe]
  );

  // Set top traders wallet to the clicked one and show marks on chart
  const onTradeClick = useCallback(
    ({ wallet }: ITopWalletsResponseItem) => {
      if (wallet !== tradesWallet) {
        dispatch(actionSetTradesWallet(wallet));
        removeMarks?.();
        drawMarks?.();
      } else {
        dispatch(actionSetTradesWallet(null));
        removeMarks?.();
      }
    },
    [dispatch, drawMarks, removeMarks, tradesWallet]
  );

  return isMobile ? (
    <TopTradersTableMobile
      chain={chain}
      exchange={exchange}
      address={address}
      loading={loading}
      wallets={collection}
      flipped={flipped}
      timezone={timezone}
    />
  ) : (
    <TopTradersTable
      exchange={exchange}
      chain={chain}
      currentWallet={tradesWallet || currentWallet}
      address={address}
      loading={loading}
      wallets={collection}
      flipped={flipped}
      plan={plan}
      timezone={timezone}
      timeframe={timeframe}
      showWatchlist={!!authToken}
      width={width}
      onTradeClick={onTradeClick}
      openWalletModal={openWalletModal!}
      handleSetTimeframe={onSetTimeframe}
    />
  );
};

export default TopTradersDataLayer;
